import React, { useEffect } from "react";
import { createBrowserRouter, RouterProvider, Navigate } from "react-router-dom";
import DoctorLogIn from "../pages/Doctor/DoctorLogIn";
import Tracking from "../pages/Doctor/Tracking";
import Account from "../pages/Doctor/Account";
import WorkspaceHub from "../pages/Doctor/WorkspaceHub";
import Dashboard from "../pages/Doctor/Dashboard";
import HealthMap from "../pages/Doctor/HealthMap";
import Planner from "../pages/Doctor/Planner";
import Profile from "../pages/Admin/Profile";
import Inventory from "../pages/Admin/Inventory";
import StoreInventory from "../pages/Store/StoreInventory";
import AdminSidebar from "../components/Admin/Inventory/AdminSidebar";
import PrescriptionModerationPage from "../pages/Admin/PrescriptionModeration";
import DoctorLayout from "../components/Doctor/DoctorLayout";
import StoreLayout from "../components/Store/StoreLayout";
import StoreLogin from "../pages/Store/StoreLogIn";
import AdminLogin from "../pages/Admin/AdminLogIn";
import Home from "../components/home";
import StoreDashboard from "../pages/Store/StoreDashboard";
import StoreRecordsPage from "../pages/Store/StoreRecordsPage";
import StoreAdminDashboard from "../pages/Admin/StoreAdminDashboard";
import WebHome from "../pages/Website/WebHome";
import TrackOrder from "../pages/Website/TrackOrder";
import Partner from "../pages/Website/Partner";
import PartnerCalc from "../pages/Website/PartnerCalc";
import SmartClinicOnboarding from "../pages/Website/SmartClinicOnboarding";
import TermsPage from "../pages/Website/TermsPage";
import PrivacyPolicy from "../pages/Website/PrivacyPolicy";
import ReturnPolicy from "../pages/Website/ReturnPolicy";
import ReceiptDownload from "../pages/ReceiptDownload";
import PrescriptionDownload from "../pages/PrescriptionDownload";
import { useDispatch, useSelector } from "react-redux";
import { onMessage } from "firebase/messaging";
import { messaging } from "../firebase";
import { setIsTrack } from "../redux/Reducer/Order/OrderSlice";
import { store } from "../redux/store";
import { setNotification } from "../redux/Reducer/Store/StoreSlice";

const AdminRoute = ({ children, allowedRoles }) => {
  const { role, user } = useSelector((state) => state.auth);
  const adminDetails = useSelector((state) => state?.admin?.adminDetails);

  if (!user?.token || role !== "admin") {
    return <Navigate to="/admin/login" replace />;
  }

  if (adminDetails && Object.keys(adminDetails).length > 0) {
    const adminRole = adminDetails.role || "admin";
    if (allowedRoles && !allowedRoles.includes(adminRole)) {
      return <Navigate to="/inventory" replace />;
    }
  }

  return children;
};

const AdminLayout = ({ children, allowedRoles }) => {
  const isSidebarVisible = useSelector((state) => state.admin?.isSidebarVisible);

  return (
    <AdminRoute allowedRoles={allowedRoles}>
      <div className={`transition-all duration-500 ease-in-out ${isSidebarVisible ? "pl-[96px]" : "pl-4"} min-h-[100vh] py-4 pr-4 w-full`}>
        <AdminSidebar />
        <div className="w-full">
          {children}
        </div>
      </div>
    </AdminRoute>
  );
};

const AppRoutes = () => {
  const dispatch = useDispatch();
  useEffect(() => {
    const handleNotification = (payload) => {
      if (payload?.data?.OID) {
        store.dispatch(setIsTrack(payload?.data));
      } else {
        const { title, body } = payload.notification || {};
        const orderIdMatch = body?.match(
          /Order with ID (\w+) has been successfully created/
        );
        if (orderIdMatch) {
          const orderId = orderIdMatch[1];
          store.dispatch(setNotification({ title, orderId }));
        }
      }
    };

    if (messaging) {
      const unsubscribe = onMessage(messaging, handleNotification);

      // Cleanup function (optional)
      return () => {
        if (unsubscribe) {
          unsubscribe();
        }
      };
    }
  }, [dispatch]);
  const router = createBrowserRouter([
    {
      path: "/",
      element: (
        <>
          <Home />
        </>
      ),
    },
    {
      path: "admin/login",
      element: (
        <>
          <AdminLogin />
        </>
      ),
    },
    {
      path: "doctor/login",
      element: (
        <>
          <DoctorLogIn />
        </>
      ),
    },
    {
      path: "store/login",
      element: (
        <>
          <StoreLogin />
        </>
      ),
    },
    {
      path: "healthmap",
      element: (
        <DoctorLayout>
          <HealthMap />
        </DoctorLayout>
      ),
    },
    {
      path: "studio",
      element: (
        <DoctorLayout>
          <Dashboard />
        </DoctorLayout>
      ),
    },
    {
      path: "planner",
      element: (
        <DoctorLayout>
          <Planner />
        </DoctorLayout>
      ),
    },
    {
      path: "tracking",
      element: (
        <DoctorLayout>
          <Tracking />
        </DoctorLayout>
      ),
    },
    {
      path: "hub",
      element: (
        <DoctorLayout>
          <WorkspaceHub />
        </DoctorLayout>
      ),
    },
    {
      path: "account",
      element: (
        <DoctorLayout>
          <Account />
        </DoctorLayout>
      ),
    },
    {
      path: "subscription",
      element: (
        <DoctorLayout>
          <Account defaultTab="Subscription" />
        </DoctorLayout>
      ),
    },
    {
      path: "inventory",
      element: (
        <AdminLayout allowedRoles={["admin", "manager"]}>
          <Inventory />
        </AdminLayout>
      ),
    },
    {
      path: "stores",
      element: (
        <AdminLayout allowedRoles={["admin"]}>
          <StoreAdminDashboard />
        </AdminLayout>
      ),
    },
    {
      path: "admin-profile",
      element: (
        <AdminLayout allowedRoles={["admin"]}>
          <Profile />
        </AdminLayout>
      ),
    },
    {
      path: "prescription-moderation",
      element: (
        <AdminLayout allowedRoles={["admin"]}>
          <PrescriptionModerationPage />
        </AdminLayout>
      ),
    },
    {
      path: "store-dashboard",
      element: (
        <StoreLayout>
          <StoreDashboard />
        </StoreLayout>
      ),
    },
    {
      path: "store-records",
      element: (
        <StoreLayout>
          <StoreRecordsPage />
        </StoreLayout>
      ),
    },
    {
      path: "store-inventory",
      element: (
        <StoreLayout>
          <StoreInventory />
        </StoreLayout>
      ),
    },
    {
      path: "home",
      element: (
        <div className="min-h-[100vh] w-full">
          <WebHome />
        </div>
      ),
    },
    {
      path: "trackorder",
      element: (
        <div className="min-h-[100vh] w-full">
          <TrackOrder />
        </div>
      ),
    },
    {
      path: "partner",
      element: (
        <div className="min-h-[100vh] w-full">
          <Partner />
        </div>
      ),
    },
    {
      path: "partner/calc",
      element: (
        <div className="min-h-[100vh] w-full">
          <PartnerCalc />
        </div>
      ),
    },
    {
      path: "smartclinic",
      element: (
        <div className="min-h-[100vh] w-full">
          <SmartClinicOnboarding />
        </div>
      ),
    },
    {
      path: "terms",
      element: (
        <div className="min-h-[100vh] w-full">
          <TermsPage />
        </div>
      ),
    },
    {
      path: "privacypolicy",
      element: (
        <div className="min-h-[100vh] w-full">
          <PrivacyPolicy />
        </div>
      ),
    },
    {
      path: "returnpolicy",
      element: (
        <div className="min-h-[100vh] w-full">
          <ReturnPolicy />
        </div>
      ),
    },
    {
      path: "r/:encoded",
      element: <ReceiptDownload />,
    },
    {
      path: "p/:encoded",
      element: <PrescriptionDownload />,
    },
  ]);
  return <RouterProvider router={router} />;
};

export default AppRoutes;
